import { TotalResults } from '@shared/components/ui/TotalResults';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAgGridColumns } from '../../hooks/useAgGridColumns';
import { useInvoicesViewMain } from '../../hooks/useInvoicesViewMain';
import { useInvoice } from '../../hooks/useInvoice';
import { InvoicesViewAgGridTable, InvoiceHeader } from '../display';
import { Breadcrumb } from '../display/Breadcrumb';
import { ErrorState, LoadingState } from '../feedback';
import { FiltersSection } from '../interaction';

export const Main = () => {
  const { t } = useTranslation('invoicesViewModule');
  const { invoice, isLoading: isLoadingInvoice } = useInvoice();

  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const {
    shipments,
    storageTableRows,
    isLoading,
    isFetching,
    error,
    total,
    invoicesViewViewProps,
    handleFilterChange,
    handleSearch,
    handleColumnsChange,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    hasActiveFilters,
    filterConditions,
    searchTerm,
    selectedColumns,
    isStorageType,
    invoiceId,
    searchFilter,
    sorting,
  } = useInvoicesViewMain();

  useEffect(() => {
    if (!isLoading && isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const { tanStackColumns } = useAgGridColumns({ selectedColumns, isStorageType });
  const selectorColumns = useMemo(() => tanStackColumns, [tanStackColumns]);

  // 🎯 Usar las filas correctas según el tipo (shipments raw para shipping, storageTableRows para storage)
  const rowsToDisplay = isStorageType ? storageTableRows : shipments;

  return (
    <>
      <Breadcrumb invoiceCode={invoice?.code} />
      {isInitialLoad ? (
        <div className="px-6 pt-4 flex flex-col flex-1 min-h-0">
          <LoadingState message={t('loading.message', 'Cargando envíos...')} className="flex-1 min-h-0" />
        </div>
      ) : error ? (
        <div className="px-6 pt-4 flex flex-col flex-1 min-h-0">
          <ErrorState error={error} onRetry={refetch} className="flex-1 min-h-0" />
        </div>
      ) : (
        <div className="px-6 pt-4 pb-4 flex flex-col flex-1 min-h-0">
          <div className="shrink-0 space-y-4">
            {invoice && <InvoiceHeader invoice={invoice} isFetching={isLoadingInvoice} />}

            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <FiltersSection
                  handleSearch={handleSearch}
                  onFilterChange={handleFilterChange}
                  columns={selectorColumns}
                  selectedColumns={selectedColumns}
                  onColumnsChange={handleColumnsChange}
                  filterConditions={filterConditions}
                  hasTeamData={false}
                  searchTerm={searchTerm}
                  invoiceId={invoiceId}
                  searchFilter={searchFilter}
                  sorting={sorting}
                  isStorageType={isStorageType}
                />
              </div>
              <TotalResults total={total} />
            </div>
          </div>

          {isLoading && (
            <div className="flex-1 min-h-0 flex items-stretch mt-4">
              <LoadingState message={t('loading.message')} className="flex-1 min-h-0" />
            </div>
          )}

          {!isLoading && rowsToDisplay.length === 0 && (
            <div className="flex-1 min-h-0 flex flex-col justify-center mt-4">
              <EmptyView type={hasActiveFilters ? 'noResults' : 'emptyState'} icon={EMPTY_VIEW_ICONS.ORDERS} />
            </div>
          )}

          {!isLoading && total > 0 && (
            <div
              className={`flex flex-col flex-1 min-h-0 mt-4 transition-opacity duration-300 ${
                isFetching && !isFetchingNextPage ? 'opacity-50 pointer-events-none' : 'opacity-100'
              }`}
            >
              <InvoicesViewAgGridTable
                {...invoicesViewViewProps}
                rows={rowsToDisplay}
                selectedColumns={selectedColumns}
                fetchNextPage={fetchNextPage}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                isStorageType={isStorageType}
              />
            </div>
          )}
        </div>
      )}
    </>
  );
};
